import { ComponentType } from '@angular/cdk/portal';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ControlContainer } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { ErrorInputFieldComponent } from '@cargos/sprintpay-models';
import { PhoneInputComponent } from '@cargos/sprintpay-ui';
import { Subject, takeUntil } from 'rxjs';
import {
    ModuleMFAEnum,
    ResponseMFA,
    TypeModuleMFA,
} from 'src/app/modules/two-factor/verification-code/models/two-factor-models';
import { TwoFactorComponent } from 'src/app/modules/two-factor/verification-code/two-factor.component';
import { UserSessionService } from 'src/app/services';
import { PersonalInformationFormGroupType } from '../../models/form-types';

@Component({
    selector: 'app-personal-information',
    templateUrl: './personal-information.component.html',
    styleUrl: './personal-information.component.scss',
    standalone: false,
})
export class PersonalInformationComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() stepper!: MatStepper;
    @Output() cancelEvent: EventEmitter<boolean> = new EventEmitter();
    @Output() dynamicResponseEvent: EventEmitter<ResponseMFA> = new EventEmitter();
    @ViewChild('phoneInputComponent') phoneInputComponent: PhoneInputComponent;
    @ViewChild('twoFactorModal') twoFactorModal?: ComponentType<TwoFactorComponent>;
    public parentForm: PersonalInformationFormGroupType;
    public errorList: ErrorInputFieldComponent[];
    private readonly unsubscribe$: Subject<void> = new Subject<void>();
    public readonly module: TypeModuleMFA = ModuleMFAEnum.FACILITY_APPLICATION_INITIATE;
    public readonly whiteListCountryCodes: string[] = ['US'];

    constructor(
        private readonly controlContainer: ControlContainer,
        private readonly matDialog: MatDialog,
        private readonly userSessionService: UserSessionService
    ) {}

    ngOnInit(): void {
        this.userSessionService.signInAsGuest().pipe(takeUntil(this.unsubscribe$)).subscribe();

        this.parentForm = this.controlContainer.control as PersonalInformationFormGroupType;

        this.errorList = [
            {
                code: 'required',
                message: `Required field`,
            },
            {
                code: 'pattern',
                message: `The email address was malformed! Please try again`,
            },
            {
                code: 'minlength',
                message: 'Should have at least 3 characters',
            },
            {
                code: 'preventedHTMLStr',
                message: 'HTML tags or URLs are not allowed',
            },
            {
                code: 'noNumbersValidator',
                message: 'Numbers are not allowed',
            },
            {
                code: 'uppercaseonly',
                message: 'Only uppercase characters are allowed',
            },
        ];
    }

    ngAfterViewInit(): void {
        this.parentForm.controls['phoneForm'].controls['code'].setValue(
            this.phoneInputComponent?.phoneForm.controls['code'].value
        );

        this.phoneInputComponent?.phoneForm
            .get('code')
            ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
            .subscribe((code) => {
                this.parentForm.controls['phoneForm'].controls['code'].setValue(code);
                this.parentForm.controls['phoneForm'].controls['code'].markAsDirty();
            });

        this.phoneInputComponent?.phoneForm
            .get('phone')
            ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
            .subscribe((code) => {
                this.parentForm.controls['phoneForm'].controls['phone'].setValue(code);
                this.parentForm.controls['phoneForm'].controls['phone'].markAsDirty();
            });
    }

    cancel(): void {
        this.cancelEvent.emit(true);
    }

    next(): void {
        if (this.parentForm.valid && this.parentForm.pristine) {
            this.stepper.next();
        } else {
            this.matDialog.open(this.twoFactorModal as ComponentType<TwoFactorComponent>, {
                id: 'two-factor',
                disableClose: false,
                data: {
                    mfaType: 'email',
                    contact: this.parentForm.controls['email'].value,
                    activationUuid: '',
                    dynamicParameter: {
                        firstName: this.parentForm.controls['firstName'].value,
                        lastName: this.parentForm.controls['lastName'].value,
                        email: this.parentForm.controls['email'].value,
                        phone: this.parentForm.controls['phoneForm'].controls['phone'].value,
                    },
                    accountVerifiedHandler: (event: ResponseMFA) => {
                        this.dynamicResponseEvent.emit(event);
                        if (event.isVerified) {
                            setTimeout(() => {
                                this.stepper.next();
                                this.parentForm.markAsPristine();
                            }, 500);
                        }
                    },
                },
            });
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
